import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Row, ColumnDef } from '@tanstack/react-table';
import { LotWithTaxMetrics, TaxRegionKey } from '@capintel/instruments-lib';

import AppRegion from '@/types/app-region';
import DataTableV2 from '@/views/components/data/DataTableV2/DataTableV2';
import { CurrencyCell, TooltipHeaderCell, PercentageCell } from '@/views/components/cells';
import NameCell from './components/NameCell';

import { getTotalRow } from '../utils/getTotalRow';
import { formatMessage, formatNumber, DEFAULT_DECIMALS } from '@/utils/i18n';

import { RowType, TaxAnalysisTableKeys, HoldingWithTaxData } from '../../types';
import { ReduxState } from '@/redux/modules/types';
import ComparisonHolding from '@/types/comparison-holding';
import NoDataText from '@/types/no-data-text';
import LocalizedString from '@/types/localized-string';
import { isUserInLotRegion } from '@/utils/tax-analysis';

type TaxAnalysisDataTableProps = {
  readOnly: boolean;
  hasTargetPortfolio?: boolean;
};

type TaxAnalysisTableCellProps = {
  row: Row<HoldingWithTaxData>;
};

const BASE_WIDTH = 80;
const COMPUTED_CELL_WIDTH = BASE_WIDTH * 2;
const LONG_CELL_WIDTH = BASE_WIDTH * 2.5;
const NAME_CELL_WIDTH = BASE_WIDTH * 3;

/**
 * TaxAnalysisDataTable displays tax-related information for portfolio holdings.
 */
const TaxAnalysisDataTable: React.FC<TaxAnalysisDataTableProps> = ({ readOnly }) => {
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const user = useSelector((state: ReduxState) => state.auth.user);
  const originalComparisonItemId = activeComparison?.taxAnalysis?.originalComparisonItemId;
  const taxRegion = activeComparison?.taxAnalysis?.taxSettings?.region;
  const isInLotRegion = isUserInLotRegion(user);

  // Find the comparison item and get holdings with tax metrics from Redux
  const comparisonItem = activeComparison?.items.find(item => item._id === originalComparisonItemId);
  const holdingsFromRedux = comparisonItem?.holdings || [];

  // Transform holdings to table format
  const holdings: HoldingWithTaxData[] = useMemo(
    () =>
      holdingsFromRedux.map((holding: ComparisonHolding) => ({
        _id: holding.id,
        ticker: holding.symbol || '',
        name: holding.name,
        purchaseDate: holding.taxSettings?.purchaseDate,
        units: holding.units || 0,
        costBasis: holding.taxSettings?.costBasis,
        marketValue: holding.taxMetrics?.marketValue,
        currentAllocation: holding.taxMetrics?.currentAllocation,
        // Region-specific unrealized gains
        [AppRegion.US]: holding.taxMetrics?.US,
        [AppRegion.CAN]: holding.taxMetrics?.CAN,
        estimatedRealizedGainLoss: holding.taxMetrics?.estimatedRealizedGainLoss,
        taxCostToReachTarget: holding.taxMetrics?.taxCostToReachTarget,
        valueChange: holding.taxMetrics?.valueChange,
        comparisonItemId: originalComparisonItemId || '',
        lots: holding.lots,
      })),
    [holdingsFromRedux, originalComparisonItemId],
  );

  const isLotRow = (row: Row<HoldingWithTaxData>) => row.depth > 0;

  // Transform lots to match HoldingWithTaxData structure for table display
  const transformLotsToHoldingData = (lots: LotWithTaxMetrics[], comparisonItemId: string, holdingName?: LocalizedString): HoldingWithTaxData[] =>
    lots.map(lot => ({
      _id: lot._id || '',
      name: holdingName,
      purchaseDate: lot.purchaseDate,
      units: lot.units,
      costBasis: lot.costBasis,
      marketValue: lot?.taxMetrics?.marketValue,
      // Lots only have US-specific fields (lots not supported for CA)
      [AppRegion.US]: {
        shortTermUnrealizedGainLoss: lot?.taxMetrics?.shortTermUnrealizedGainLoss,
        longTermUnrealizedGainLoss: lot?.taxMetrics?.longTermUnrealizedGainLoss,
      },
      estimatedRealizedGainLoss: lot?.taxMetrics?.estimatedRealizedGainLoss,
      taxCostToReachTarget: lot?.taxMetrics?.taxCostToReachTarget,
      valueChange: lot?.taxMetrics?.valueChange,
      comparisonItemId,
    }));

  // Get sub-rows for expandable holdings
  const getSubRows = (row: HoldingWithTaxData): HoldingWithTaxData[] | undefined => {
    // Don't allow expanding the total row
    if (row._id === totalRow._id) return undefined;
    if (!row.lots) return undefined;

    return transformLotsToHoldingData(row.lots, row.comparisonItemId, row.name);
  };

  const columns = useMemo<ColumnDef<HoldingWithTaxData>[]>(() => {
    const tooltipHeader = (labelKey: string, tooltipKey: string) => () => (
      <TooltipHeaderCell label={formatMessage(labelKey)} tooltipMessage={formatMessage(tooltipKey)} />
    );

    const columnMap: Record<string, ColumnDef<HoldingWithTaxData>> = {
      ticker: {
        accessorKey: TaxAnalysisTableKeys.Ticker,
        cell: ({ row }: TaxAnalysisTableCellProps) => (isLotRow(row) ? null : row.original.ticker),
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.Ticker'),
        meta: { customClass: 'sticky-col sticky-col-ticker' },
        size: BASE_WIDTH,
      },
      name: {
        accessorKey: TaxAnalysisTableKeys.Name,
        cell: ({ row }: TaxAnalysisTableCellProps) => <NameCell name={row.original.name} />,
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.Name'),
        meta: { customClass: 'sticky-col sticky-col-name' },
        size: NAME_CELL_WIDTH,
      },
      purchaseDate: {
        accessorKey: TaxAnalysisTableKeys.PurchaseDate,
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.PurchaseDate'),
        meta: { customClass: 'tax-analysis-purchase-date-col' },
        size: COMPUTED_CELL_WIDTH,
        cell: ({ row }: TaxAnalysisTableCellProps) => {
          if (row.id === RowType.Total || !row.original.purchaseDate) return '';
          return new Date(row.original.purchaseDate).toLocaleDateString();
        },
      },
      units: {
        accessorKey: TaxAnalysisTableKeys.Units,
        cell: ({ row }: TaxAnalysisTableCellProps) => {
          if (row.id === RowType.Total) return '';
          return formatNumber(row.original.units ?? 0, DEFAULT_DECIMALS);
        },
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.Units'),
        size: COMPUTED_CELL_WIDTH,
      },
      costBasis: {
        accessorKey: TaxAnalysisTableKeys.CostBasis,
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.CostBasis'),
        cell: ({ row }: TaxAnalysisTableCellProps) => {
          if (row.id === RowType.Total) return '';
          return <CurrencyCell value={{ value: row.original.costBasis }} noDataText={NoDataText.Dash} />;
        },
        size: COMPUTED_CELL_WIDTH,
      },
      marketValue: {
        accessorKey: TaxAnalysisTableKeys.MarketValue,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[TaxAnalysisTableKeys.MarketValue] }} noDataText={NoDataText.Dash} />
        ),
        header: formatMessage('Comparison.TaxAnalysis.ColumnHeaders.MarketValue'),
        size: COMPUTED_CELL_WIDTH,
      },
      caUnrealizedGainLoss: {
        accessorKey: TaxAnalysisTableKeys.UnrealizedGainLoss,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[AppRegion.CAN]?.unrealizedGainLoss }} noDataText={NoDataText.Dash} />
        ),
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.UnrealizedGainLoss', 'Comparison.TaxAnalysis.Tooltips.UnrealizedGainLoss'),
        size: COMPUTED_CELL_WIDTH,
      },
      shortTermUnrealized: {
        accessorKey: TaxAnalysisTableKeys.ShortTermUnrealizedGainLoss,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[AppRegion.US]?.shortTermUnrealizedGainLoss }} noDataText={NoDataText.Dash} />
        ),
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.STUnrealizedGainLoss', 'Comparison.TaxAnalysis.Tooltips.STUnrealizedGainLoss'),
        size: COMPUTED_CELL_WIDTH,
      },
      longTermUnrealized: {
        accessorKey: TaxAnalysisTableKeys.LongTermUnrealizedGainLoss,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[AppRegion.US]?.longTermUnrealizedGainLoss }} noDataText={NoDataText.Dash} />
        ),
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.LTUnrealizedGainLoss', 'Comparison.TaxAnalysis.Tooltips.LTUnrealizedGainLoss'),
        size: COMPUTED_CELL_WIDTH,
      },
      totalUnrealized: {
        accessorKey: TaxAnalysisTableKeys.TotalUnrealizedGainLoss,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[AppRegion.US]?.totalUnrealizedGainLoss }} noDataText={NoDataText.Dash} />
        ),
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.TotalUnrealizedGainLoss', 'Comparison.TaxAnalysis.Tooltips.TotalUnrealizedGainLoss'),
        size: LONG_CELL_WIDTH,
      },
      currentAllocation: {
        accessorKey: TaxAnalysisTableKeys.CurrentAllocation,
        cell: ({ row }: TaxAnalysisTableCellProps) => {
          const value = row.original[TaxAnalysisTableKeys.CurrentAllocation] != null ? row.original[TaxAnalysisTableKeys.CurrentAllocation] * 100 : null;
          return <PercentageCell value={value} noDataText={NoDataText.Dash} />;
        },
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.CurrentAllocation', 'Comparison.TaxAnalysis.Tooltips.CurrentAllocation'),
        size: COMPUTED_CELL_WIDTH,
      },
      valueChange: {
        accessorKey: TaxAnalysisTableKeys.ValueChange,
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.ValueChange', 'Comparison.TaxAnalysis.Tooltips.ValueChange'),
        size: COMPUTED_CELL_WIDTH,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[TaxAnalysisTableKeys.ValueChange] }} noDataText={NoDataText.Dash} />
        ),
      },
      estimatedRealized: {
        accessorKey: TaxAnalysisTableKeys.EstimatedRealizedGainLoss,
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.EstimatedRealizedGainLoss', 'Comparison.TaxAnalysis.Tooltips.EstimatedRealizedGainLoss'),
        size: LONG_CELL_WIDTH,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[TaxAnalysisTableKeys.EstimatedRealizedGainLoss] }} noDataText={NoDataText.Dash} />
        ),
      },
      taxCost: {
        accessorKey: TaxAnalysisTableKeys.TaxCostToReachTarget,
        header: tooltipHeader('Comparison.TaxAnalysis.ColumnHeaders.TaxCostToReachTarget', 'Comparison.TaxAnalysis.Tooltips.TaxCostToReachTarget'),
        size: COMPUTED_CELL_WIDTH,
        cell: ({ row }: TaxAnalysisTableCellProps) => (
          <CurrencyCell value={{ value: row.original[TaxAnalysisTableKeys.TaxCostToReachTarget] }} noDataText={NoDataText.Dash} />
        ),
      },
    };

    if (!taxRegion) {
      return [];
    }

    const columnsByRegion: Record<TaxRegionKey, ColumnDef<HoldingWithTaxData>[]> = {
      [TaxRegionKey.CA]: [
        columnMap.ticker,
        columnMap.name,
        columnMap.units,
        columnMap.costBasis,
        columnMap.marketValue,
        columnMap.caUnrealizedGainLoss,
        columnMap.currentAllocation,
        columnMap.valueChange,
        columnMap.estimatedRealized,
        columnMap.taxCost,
      ],
      [TaxRegionKey.US]: [
        columnMap.ticker,
        columnMap.name,
        columnMap.purchaseDate,
        columnMap.units,
        columnMap.costBasis,
        columnMap.marketValue,
        columnMap.shortTermUnrealized,
        columnMap.longTermUnrealized,
        columnMap.totalUnrealized,
        columnMap.currentAllocation,
        columnMap.valueChange,
        columnMap.estimatedRealized,
        columnMap.taxCost,
      ],
    };

    return columnsByRegion[taxRegion];
  }, [taxRegion, isLotRow]);

  const totalRow = useMemo(() => getTotalRow(holdings), [holdings]);
  const dataWithTotal = useMemo(() => [totalRow, ...holdings], [totalRow, holdings]);

  return (
    <div className="tax-analysis-table-wrapper">
      <DataTableV2
        columns={columns}
        data={dataWithTotal}
        enablePagination
        enableSorting
        isExpandable={isInLotRegion}
        getAdditionalRowClassNames={row => (row.original && row.original._id === totalRow._id ? 'tax-analysis-total-row' : '')}
        getSubRows={(originalRow: HoldingWithTaxData) => {
          if (!isInLotRegion) return undefined;
          return originalRow.lots && originalRow.lots.length > 0 ? getSubRows(originalRow) : undefined;
        }}
      />
    </div>
  );
};

export default TaxAnalysisDataTable;
